import React, { useEffect, useState, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { MaintenanceModeDialog } from 'app/components/MaintenanceModeDialog';
import { checkMaintenanceStatus } from 'app/api/maintenance';
import { getUserType, UserType } from 'app/utils/userType';

const FAIL_OPEN_TIMEOUT_MS = 1500;
const POLL_INTERVAL_MS = 30 * 1000; // 30 seconds

interface Props {
  children: ReactNode;
}

/**
 * Check if the URL has the bypass_maintenance parameter for admin access
 */
function hasBypassParam(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('bypass_maintenance');
}

/**
 * Global gate that blocks the app when the backend is in maintenance mode.
 * 
 * Behavior:
 * - On mount: checks /maintenance/status (max 1500ms timeout, then fail-open)
 * - If maintenance mode is active → show MaintenanceModeDialog
 * - Bypass: add ?bypass_maintenance to URL (e.g., /auth/login?bypass_maintenance)
 * - Admin users (userType includes 'admin') bypass maintenance
 * - If not in maintenance → render children (app)
 * - When in maintenance mode, auto-polls every 30 seconds to check if maintenance ended
 * - On maintenance end: shows toast and reloads the page
 * - Listens for maintenanceTriggered events from API interceptor (for mid-session triggers)
 */
export function MaintenanceModeGate({ children }: Props) {
  const [ready, setReady] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [expectedEndTime, setExpectedEndTime] = useState<string | null>(null);

  // Check if current user is an admin based on userType
  const user = useSelector((state: any) => state.auth.user);
  const userTypes = user ? getUserType(user) : [];
  const isAdmin = userTypes.includes(UserType.Admin);

  // Initial maintenance status check on mount
  useEffect(() => {
    let cancelled = false;

    const checkStatus = async () => {
      try {
        const status = await checkMaintenanceStatus();
        if (cancelled) return;

        if (status.maintenanceMode) {
          setMaintenanceEnabled(true);
          setExpectedEndTime(status.expectedEndTime);
        }
        setReady(true);
      } catch (error) {
        if (cancelled) return;
        // Fail-open on error
        setReady(true);
      }
    };

    checkStatus();

    // Fail-open timeout
    const timeoutId = setTimeout(() => {
      setTimedOut(true);
      if (!ready) {
        setReady(true);
      }
    }, FAIL_OPEN_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Only run on mount; intentionally empty deps to avoid re-checking on state changes

  // Listen for maintenance mode triggered by API interceptor
  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<{ expectedEndTime: string | null }>;
      setMaintenanceEnabled(true);
      setExpectedEndTime(customEvent.detail?.expectedEndTime || null);
    };

    window.addEventListener('maintenanceTriggered', handler);
    return () => window.removeEventListener('maintenanceTriggered', handler);
  }, []);

  // Poll for maintenance status when in maintenance mode
  useEffect(() => {
    if (!maintenanceEnabled) return;

    const pollStatus = async () => {
      try {
        const status = await checkMaintenanceStatus();
        if (status.maintenanceMode) {
          // Update expected end time if it changed
          setExpectedEndTime(status.expectedEndTime);
        } else if (status.status === 'OK') {
          // Only exit maintenance if we got an actual successful response (not a fail-open default)
          // This prevents false "maintenance complete" when backend is completely down
          toast.success('Maintenance complete. Reloading...', { autoClose: 2000 });
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
        // If status is 'ERROR', the request failed - keep showing maintenance and continue polling
      } catch (error) {
        // On error, continue polling (don't exit maintenance mode)
        console.error('Failed to check maintenance status:', error);
      }
    };

    // Poll immediately, then set interval
    pollStatus();
    const intervalId = setInterval(pollStatus, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [maintenanceEnabled]);

  // Still waiting for initial check and timeout hasn't expired
  if (!ready && !timedOut) {
    return null;
  }

  // Admin users (userType includes 'admin') bypass maintenance
  if (maintenanceEnabled && isAdmin) {
    return <>{children}</>;
  }

  // Allow bypass via URL parameter (e.g., /auth/login?bypass_maintenance)
  // This allows admins to access the login page during maintenance
  if (maintenanceEnabled && hasBypassParam()) {
    return <>{children}</>;
  }

  // Show maintenance dialog if maintenance mode is enabled
  if (maintenanceEnabled) {
    return <MaintenanceModeDialog expectedEndTime={expectedEndTime} />;
  }

  return <>{children}</>;
}
